/**
 * THE canonical answer to "what is Nuvra running inside right now".
 *
 * Nuvra ships as ONE product on two surfaces: the web app, and the same bundle wrapped in an
 * Android shell (see capacitor.config.ts). Almost everything is identical across them. The places
 * that are not, above all billing, must branch on exactly one authority, because the alternative
 * is a scatter of `window.Capacitor` checks that drift apart and eventually leave a web payment
 * button rendering inside the Play build. That single divergence is a Play policy violation and a
 * takedown, so this file exists to make the question unanswerable anywhere else.
 *
 * SCOPE, and the distinction is load-bearing: this is about NUVRA ITSELF. It has nothing to do
 * with `src/server/mobile/`, which packages applications Nuvra GENERATES FOR CUSTOMERS. A
 * customer's Android build must never be able to influence how our own store build behaves.
 *
 * Detection reads the bridge object the native runtime injects into the WebView. On the web that
 * object is simply absent, so the web app answers "web" without loading anything Android-specific
 * and behaves exactly as it did before this file existed.
 */

/** The surfaces Nuvra itself can run on. "web" is every browser, desktop or mobile. */
export type AppPlatform = "web" | "android" | "ios";

/** The shape the native bridge injects. Only the fields we actually rely on. */
interface CapacitorBridge {
  getPlatform?: () => string;
  isNativePlatform?: () => boolean;
  platform?: string;
  isNative?: boolean;
}

interface PlatformGlobal {
  Capacitor?: CapacitorBridge;
}

/**
 * Pure detection. Exported so tests can assert every branch without a browser, and so nothing has
 * to mutate globals to prove behaviour.
 *
 * Only "android" and "ios" are recognised as native. Anything else, including an unknown future
 * platform string, resolves to "web". That default direction is deliberate: guessing "web" on an
 * unrecognised surface shows web checkout somewhere it might not belong, which is a bug we would
 * see; guessing "native" would silently hide billing from real paying web customers, which is
 * revenue loss we might not.
 *
 * The billing policy layer closes the remaining half of that trade by refusing to enable web
 * checkout for any surface it cannot positively identify as the web.
 */
export function detectPlatform(scope: PlatformGlobal | undefined | null): AppPlatform {
  const bridge = scope?.Capacitor;
  if (!bridge) return "web";

  let raw: string | undefined;
  try {
    raw = typeof bridge.getPlatform === "function" ? bridge.getPlatform() : bridge.platform;
  } catch {
    // A bridge that throws is a broken bridge, not a native platform.
    return "web";
  }

  if (raw === "android") return "android";
  if (raw === "ios") return "ios";
  return "web";
}

/**
 * Resolved once. The platform cannot change during a session (the shell is fixed at launch), and
 * caching means a hot render path never pays for the lookup or, worse, gets a different answer
 * partway through a render and produces inconsistent UI.
 */
let cached: AppPlatform | null = null;

export function currentPlatform(): AppPlatform {
  if (cached === null) {
    cached = detectPlatform(typeof globalThis === "undefined" ? null : (globalThis as PlatformGlobal));
  }
  return cached;
}

/** Test-only reset. Production never calls this; the platform is fixed for a session. */
export function __resetPlatformCacheForTests(): void {
  cached = null;
}

export function isWeb(): boolean {
  return currentPlatform() === "web";
}

export function isAndroid(): boolean {
  return currentPlatform() === "android";
}

export function isIos(): boolean {
  return currentPlatform() === "ios";
}

/** True on any native shell. Use this for shell concerns (back button, insets), never for billing. */
export function isNative(): boolean {
  return !isWeb();
}
